import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ViewAsContext = createContext(null);

export function ViewAsProvider({ children }) {
  const [viewingAs, setViewingAs] = useState(null);
  const [originalUser, setOriginalUser] = useState(null);
  const [effectivePermissions, setEffectivePermissions] = useState(null);
  const [viewAsLoading, setViewAsLoading] = useState(true); // NEW: Loading state

  // Check view-as status on mount
  useEffect(() => {
    checkViewAsStatus();
  }, []);

  const checkViewAsStatus = async () => {
    setViewAsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setViewAsLoading(false);
        return;
      }
      
      // First, check localStorage for view-as state (faster)
      const viewingAsStr = localStorage.getItem('viewing_as');
      if (viewingAsStr) {
        try {
          const viewingAsData = JSON.parse(viewingAsStr);
          console.log('ViewAsContext: Loaded from localStorage:', viewingAsData);
          console.log('ViewAsContext: permission_level:', viewingAsData.permission_level);
          console.log('ViewAsContext: nav_permissions:', viewingAsData.permission_level?.nav_permissions);
          setViewingAs(viewingAsData);
          // Set effective permissions from the stored permission_level
          if (viewingAsData.permission_level) {
            setEffectivePermissions(viewingAsData.permission_level);
          }
          // Store original user if we have it
          const originalUserStr = localStorage.getItem('original_user');
          if (originalUserStr) {
            setOriginalUser(JSON.parse(originalUserStr));
          }
          setViewAsLoading(false);
          return; // Use localStorage data instead of API call
        } catch (e) {
          // Invalid JSON, clear it
          localStorage.removeItem('viewing_as');
        }
      }

      // Fallback: check via API
      const response = await axios.get(`${API}/api/users/current/view-as-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.viewing_as) {
        setViewingAs(response.data.viewing_as);
        setEffectivePermissions(response.data.viewing_as.permission_level);
        
        // Store original user
        const userStr = localStorage.getItem('user');
        if (userStr) {
          setOriginalUser(JSON.parse(userStr));
        }
      }
      setViewAsLoading(false);
    } catch (error) {
      console.error('Failed to check view-as status:', error);
      setViewAsLoading(false);
    }
  };

  const startViewingAs = async (targetUserId, targetUserEmail) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/users/view-as/${targetUserId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Store original user before switching
        const userStr = localStorage.getItem('user');
        if (userStr) {
          setOriginalUser(JSON.parse(userStr));
        }

        setViewingAs(response.data.viewing_as);
        setEffectivePermissions(response.data.viewing_as.permission_level);
        
        toast.success(`Now viewing as ${targetUserEmail}`);
        
        // Force reload to apply new permissions
        window.location.reload();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start view-as mode');
    }
  };

  const stopViewingAs = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Call backend to clear the viewing_as_user_id from the database
      await axios.post(
        `${API}/api/users/stop-view-as`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Clear local storage
      localStorage.removeItem('viewing_as');
      
      setViewingAs(null);
      setEffectivePermissions(null);
      setOriginalUser(null);
      
      toast.success('Returned to your admin session');
      
      // Force reload to restore original permissions
      window.location.reload();
    } catch (error) {
      console.error('Failed to stop view-as mode:', error);
      toast.error('Failed to stop view-as mode');
    }
  };

  const getEffectivePermissions = useCallback(() => {
    if (viewingAs && effectivePermissions) {
      return effectivePermissions;
    }
    return null;
  }, [viewingAs, effectivePermissions]);

  return (
    <ViewAsContext.Provider value={{
      viewingAs,
      originalUser,
      effectivePermissions,
      startViewingAs,
      stopViewingAs,
      getEffectivePermissions,
      isViewingAsOther: !!viewingAs,
      viewAsLoading  // NEW: Expose loading state
    }}>
      {children}
    </ViewAsContext.Provider>
  );
}

export function useViewAs() {
  const context = useContext(ViewAsContext);
  if (!context) {
    throw new Error('useViewAs must be used within a ViewAsProvider');
  }
  return context;
}

// Banner component to show when viewing as another user
export function ViewAsBanner() {
  const { viewingAs, stopViewingAs, isViewingAsOther } = useViewAs();

  if (!isViewingAsOther) return null;

  return (
    <div className="fixed top-0 left-0 right-0 bg-amber-500 text-amber-950 py-2 px-4 z-[100] flex items-center justify-between">
      <div className="flex items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
        <span className="font-medium">
          Viewing as: <strong>{viewingAs?.name || viewingAs?.email || 'employee'}</strong>
          {viewingAs?.simulated && (
            <span className="ml-2 text-[11px] uppercase tracking-wider bg-amber-700 text-amber-50 px-1.5 py-0.5 rounded">
              Simulated
            </span>
          )}
        </span>
        <span className="text-amber-800 text-sm">
          ({viewingAs?.simulated ? 'No real login linked — using default employee permissions' : 'You are seeing what this user sees'})
        </span>
      </div>
      <button
        onClick={stopViewingAs}
        className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
      >
        Exit View As
      </button>
    </div>
  );
}
